import { useEffect, useMemo, useRef, useState } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { navSettingsStore } from '../lib/navSettingsStore'
import { avatarFloor } from '../lib/avatarFloor'

/**
 * AgentCapsuleGizmo — the character's navigation capsule (ENG-591), drawn as a
 * GREEN wireframe so it reads distinctly from the navmesh (blue) and collision
 * mesh (orange). radius/height come from the Bake panel (bakeSettingsStore); the
 * capsule follows the avatar each frame (window.__AVATAR_GROUP__), so you can see
 * exactly the volume the navmesh carve erodes/clears for.
 *
 * Visible by default; toggle via the panel's "show" checkbox.
 */
export default function AgentCapsuleGizmo() {
  const ref = useRef(null)
  const [, force] = useState(0)
  useEffect(() => navSettingsStore.subscribe(() => force(n => n + 1)), [])
  const { params, show } = navSettingsStore.getState()
  const radius = params.agentRadius ?? 0.25
  const height = params.agentHeight ?? 1.7

  // CapsuleGeometry length is the cylinder part; total height = length + 2*radius.
  const geom = useMemo(() => {
    const len = Math.max(0.01, height - 2 * radius)
    return new THREE.CapsuleGeometry(radius, len, 6, 16)
  }, [radius, height])
  useEffect(() => () => geom.dispose(), [geom])

  useFrame(() => {
    const m = ref.current
    if (!m) return
    const g = (typeof window !== 'undefined' && window.__AVATAR_GROUP__) || null
    const floorY = avatarFloor.getY?.() ?? 0
    if (g) {
      m.position.set(g.position.x, floorY + height / 2, g.position.z)
    } else {
      m.position.set(0, floorY + height / 2, 0)
    }
  })

  // Capsule is an authoring aid — only while the Build Navmesh panel is open.
  if (!show.capsule) return null
  return (
    <mesh ref={ref} geometry={geom} renderOrder={9998}>
      <meshBasicMaterial color={0x34d399} wireframe transparent opacity={0.6} depthTest={false} depthWrite={false} />
    </mesh>
  )
}
